import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { sendPushBroadcast } from "@/lib/push-broadcast.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/admin/push")({
  head: () => ({
    meta: [
      { title: "Отправить сообщение — админка TurkeyDrive" },
      { name: "description", content: "Сообщение резидентам TurkeyDrive: в ленту уведомлений и пушем." },
      { property: "og:title", content: "Отправить сообщение — админка TurkeyDrive" },
      { property: "og:description", content: "Сообщение резидентам TurkeyDrive: в ленту уведомлений и пушем." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPushPage,
});

type Audience = "all" | "event" | "circle" | "event_topics" | "event_topics_all" | "profile_gaps";
const AUDIENCE_LABEL: Record<Audience, string> = {
  all: "Все, кто включил уведомления",
  event: "Участникам мероприятия",
  circle: "Участникам сообщества",
  event_topics: "Кому подходит по интересам — в городе мероприятия",
  event_topics_all: "Кому подходит по интересам — по всей Турции",
  profile_gaps: "Кто не заполнил город или интересы",
};
const HISTORY_LABEL: Record<string, string> = {
  all: "всем резидентам",
  event: "участникам мероприятия",
  circle: "участникам сообщества",
  event_topics: "по интересам, город",
  event_topics_all: "по интересам, вся Турция",
  profile_gaps: "не заполнили город или интересы",
};

function plPeople(n: number) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "человек";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "человека";
  return "человек";
}
function plForm(n: number, one: string, few: string, many: string) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
const plDevices = (n: number) => plForm(n, "устройство", "устройства", "устройств");
const plResidentsDat = (n: number) => plForm(n, "резиденту", "резидентам", "резидентам");
const plResidentsGen = (n: number) => plForm(n, "резидент", "резидента", "резидентов");

function AdminPushPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const send = useServerFn(sendPushBroadcast);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [audience, setAudience] = useState<Audience>("all");
  const [eventId, setEventId] = useState<string>("");
  const [circleId, setCircleId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) return navigate({ to: "/home" });
      const { data: prof } = await supabase.from("profiles").select("is_admin").eq("user_id", uid).maybeSingle();
      if (!(prof as { is_admin?: boolean } | null)?.is_admin) return navigate({ to: "/home" });
      setAuthorized(true);
    })();
  }, [navigate]);

  const enabled = authorized === true;

  const { data: events = [] } = useQuery({
    queryKey: ["admin", "push", "events"],
    enabled,
    queryFn: async () => {
      const { data } = await supabase
        .from("events")
        .select("id, title, event_date, topics")
        .eq("status", "published")
        .order("event_date", { ascending: false })
        .limit(100);
      return data ?? [];
    },
  });
  const { data: circles = [] } = useQuery({
    queryKey: ["admin", "push", "circles"],
    enabled,
    queryFn: async () => {
      const { data } = await supabase
        .from("circles")
        .select("id, name")
        .eq("status", "published")
        .eq("is_deleted", false)
        .order("name");
      return data ?? [];
    },
  });

  const isEventAud = audience === "event" || audience === "event_topics" || audience === "event_topics_all";
  const isTopicsAud = audience === "event_topics" || audience === "event_topics_all";
  const selectedEvent = events.find((e) => e.id === eventId);
  const eventTopics = selectedEvent?.topics ?? [];
  const topicsMissing = isTopicsAud && !!selectedEvent && eventTopics.length === 0;
  const targetMissing = (isEventAud && !eventId) || (audience === "circle" && !circleId);

  const { data: size, isFetching: sizeLoading } = useQuery({
    queryKey: ["admin", "push", "size", audience, eventId, circleId],
    enabled: enabled && !targetMissing,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("push_audience_size", {
        p_audience: audience,
        p_event_id: isEventAud ? eventId : null,
        p_circle_id: audience === "circle" ? circleId : null,
      } as never);
      if (error) throw error;
      return (data as number) ?? 0;
    },
  });
  const n = targetMissing ? 0 : (size ?? 0);

  const { data: feedSize, isFetching: feedLoading } = useQuery({
    queryKey: ["admin", "push", "feed-size", audience, eventId, circleId],
    enabled: enabled && !targetMissing,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("notification_audience_size", {
        p_audience: audience,
        p_event_id: isEventAud ? eventId : undefined,
        p_circle_id: audience === "circle" ? circleId : undefined,
      });
      if (error) throw error;
      return (data as number) ?? 0;
    },
  });
  const f = targetMissing ? 0 : (feedSize ?? 0);

  const { data: history = [] } = useQuery({
    queryKey: ["admin", "push", "history"],
    enabled,
    queryFn: async () => {
      const { data } = await supabase
        .from("push_broadcasts")
        .select("id, title, audience, recipients, delivered, sent_at, feed_recipients")
        .order("sent_at", { ascending: false })
        .limit(30);
      return data ?? [];
    },
  });

  const canSend = !!title.trim() && !!body.trim() && !targetMissing && !topicsMissing && f > 0 && !sending;

  async function doSend() {
    setSending(true);
    try {
      const { data: created, error: feedErr } = await supabase.rpc("broadcast_notification", {
        p_audience: audience,
        p_title: title.trim(),
        p_body: body.trim(),
        p_url: url.trim() || undefined,
        p_event_id: isEventAud ? eventId : undefined,
        p_circle_id: audience === "circle" ? circleId : undefined,
      });
      if (feedErr) throw new Error("Не удалось отправить сообщение в ленту");
      const feedCount = (created as number) ?? 0;
      let delivered = 0;
      let pushOk = true;
      try {
        const res = await send({
        data: {
          audience,
          eventId: isEventAud ? eventId : null,
          circleId: audience === "circle" ? circleId : null,
          title: title.trim(),
          body: body.trim(),
          url: url.trim() || null,
          feedRecipients: feedCount,
        },
        });
        delivered = res.delivered;
      } catch (pushErr) {
        console.error("[push] ошибка отправки пуша:", pushErr);
        pushOk = false;
      }
      if (pushOk && (delivered > 0 || n === 0)) {
        toast.success(`Отправлено: ${feedCount} ${plPeople(feedCount)} увидят в приложении, пуш доставлен на ${delivered} ${plDevices(delivered)}`);
      } else {
        toast.warning(`Отправлено ${feedCount} ${plResidentsDat(feedCount)} в приложении. Пуш отправить не удалось`);
      }
      setTitle("");
      setBody("");
      setUrl("");
      qc.invalidateQueries({ queryKey: ["admin", "push"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось отправить");
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  }

  if (!enabled) {
    return <div className="mx-auto max-w-2xl px-4 py-8 text-sm text-td-muted">Проверка доступа…</div>;
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <button type="button" onClick={() => navigate({ to: "/profile" })} className="mb-4 text-sm text-td-muted hover:text-td-dark">
        ← Назад
      </button>
      <h1 className="mb-4 text-2xl font-semibold text-td-dark">Отправить сообщение</h1>

      <div className="space-y-4 rounded-2xl border border-td-line bg-white p-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-td-dark">Кому</label>
          <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((k) => (
                <SelectItem key={k} value={k}>{AUDIENCE_LABEL[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isEventAud ? (
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger className="min-w-0"><SelectValue placeholder="Выберите мероприятие" /></SelectTrigger>
              <SelectContent>
                {events.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.event_date ? new Date(e.event_date).toLocaleDateString("ru-RU") + " · " : ""}{e.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {isTopicsAud ? (
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>Получат те, у кого в профиле указаны интересы, совпадающие с темами мероприятия. Уже записавшиеся не получат.</p>
              {selectedEvent && eventTopics.length > 0 ? (
                <p className="break-words">Темы: {eventTopics.join(", ")}</p>
              ) : null}
              {topicsMissing ? (
                <p className="font-medium text-destructive">У мероприятия не указаны темы, подобрать некого</p>
              ) : null}
            </div>
          ) : null}
          {audience === "circle" ? (
            <Select value={circleId} onValueChange={setCircleId}>
              <SelectTrigger className="min-w-0"><SelectValue placeholder="Выберите сообщество" /></SelectTrigger>
              <SelectContent>
                {circles.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {audience === "profile_gaps" ? (
            <p className="text-xs text-muted-foreground">
              Эти люди не попадают в подбор по интересам — их не находят ни мероприятия, ни знакомства
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <label className="font-medium text-td-dark">Заголовок</label>
            <span className="text-xs text-td-muted">осталось {60 - title.length}</span>
          </div>
          <Input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="Например: Завтра встреча в Анталье" />
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <label className="font-medium text-td-dark">Текст</label>
            <span className="text-xs text-td-muted">осталось {150 - body.length}</span>
          </div>
          <Textarea value={body} maxLength={150} rows={3} onChange={(e) => setBody(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-td-dark">Куда ведёт нажатие (необязательно)</label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/e/<id> — ссылка на событие" />
          <p className="text-xs text-td-muted">Если пусто — откроется главная. Можно вставить ссылку на событие вида /e/&lt;id&gt;.</p>
        </div>

        <div>
          <div className="mb-1.5 text-xs uppercase tracking-wide text-td-muted">Предпросмотр</div>
          <div className="flex items-start gap-3 rounded-2xl bg-td-soft/60 p-3 shadow-sm">
            <img loading="lazy" decoding="async" src="/icons/app-icon-192.png" alt="" className="size-10 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1">
              <div className="flex justify-between gap-2 text-xs text-td-muted">
                <span>TurkeyDrive</span><span>сейчас</span>
              </div>
              <div className="break-words text-sm font-semibold text-td-dark line-clamp-1">{title || "Заголовок уведомления"}</div>
              <div className="break-words text-sm text-td-dark/80 line-clamp-3">{body || "Текст уведомления"}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-td-line pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <div className="text-td-dark">
              Увидят в приложении: {targetMissing ? "—" : feedLoading ? "…" : `${f} ${plPeople(f)}`}
            </div>
            <div className="text-xs text-td-muted">
              Из них получат пуш на телефон: {targetMissing ? "—" : sizeLoading ? "…" : n}
            </div>
            {!targetMissing && !feedLoading && f === 0 ? (
              <div className="text-xs text-td-muted">В этой аудитории пока никого нет</div>
            ) : null}
          </div>
          <Button disabled={!canSend} onClick={() => setConfirmOpen(true)} className="w-full sm:w-auto">
            {sending ? "Отправляем…" : "Отправить"}
          </Button>
        </div>
      </div>

      <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-td-muted">История рассылок</h2>
      {history.length === 0 ? (
        <p className="text-sm text-td-muted">Рассылок пока не было.</p>
      ) : (
        <ul className="divide-y divide-td-line overflow-hidden rounded-2xl border border-td-line bg-white">
          {history.map((h) => (
            <li key={h.id} className="px-4 py-3">
              <div className="flex justify-between gap-2 text-xs text-td-muted">
                <span>{new Date(h.sent_at).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })}</span>
                <span>в ленте: {h.feed_recipients} · пуш доставлен: {h.delivered} из {h.recipients}</span>
              </div>
              <div className="break-words text-sm font-medium text-td-dark">{h.title}</div>
              <div className="text-xs text-td-muted">{HISTORY_LABEL[h.audience] ?? h.audience}</div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !sending && setConfirmOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отправить сообщение?</AlertDialogTitle>
            <AlertDialogDescription>
              Сообщение увидят {f} {plResidentsGen(f)} в приложении, из них {n} получат пуш. Отменить будет нельзя.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              disabled={sending}
              onClick={(e) => {
                e.preventDefault();
                void doSend();
              }}
            >
              {sending ? "Отправляем…" : "Отправить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
